import {
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
  type FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';
import { authService, getAppConfig } from 'framework-react-core';
import qs from 'qs';
import { VITE_API_URL } from '../utilities/env';
import { setToken } from './slices/auth.slice.ts';
import type { COIStore } from './store.tsx';

let refreshPromise: Promise<string | null> | null = null;
let lastFailedRefreshToken: string | null | undefined;

// Refresh the access token using MSAL if we get a 401 response from the API
async function refreshAccessToken(): Promise<string | null> {
  if (!authService) return null;

  try {
    const result = await authService.acquireToken();
    return result.accessToken;
  } catch {
    // No active MSAL account available for silent token retrieval
  }

  try {
    const scopes = getAppConfig().getConfigValue<{ default: string[] }>(
      'FRAMEWORK_UI_AUTH_SCOPES'
    ).default;
    const result = await authService.msalClient.ssoSilent({ scopes });
    authService.msalClient.setActiveAccount(result.account);
    return result.accessToken;
  } catch {
    // Silent SSO refresh is unavailable, preserve the original 401 response
  }

  return null;
}

async function getRefreshedAccessToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken()
      .catch(() => null)
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

type TokenRefreshApi = {
  getState: () => unknown;
  dispatch: (action: ReturnType<typeof setToken>) => unknown;
};

/**
 * Shared auth recovery used by baseApiQuery and axios upload mutations.
 * Pass the token that was actually used on the failed request so lastFailedRefreshToken
 * checks stay correct if the store token changes while the request is in flight.
 * Returns a fresh access token after dispatching it into the store, or null if refresh failed.
 */
export async function tryRefreshAuthToken(
  api: TokenRefreshApi,
  failedRequestToken: string | null
): Promise<string | null> {
  // Avoid repeatedly attempting refresh for the same token that already failed to refresh
  if (lastFailedRefreshToken !== undefined && failedRequestToken === lastFailedRefreshToken) {
    return null;
  }

  const refreshedAccessToken = await getRefreshedAccessToken();

  if (!refreshedAccessToken) {
    lastFailedRefreshToken = failedRequestToken;
    return null;
  }

  lastFailedRefreshToken = undefined;
  api.dispatch(setToken({ token: refreshedAccessToken }));
  return refreshedAccessToken;
}

export const baseApiQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  Record<string, unknown>,
  FetchBaseQueryMeta
> = (() => {
  const query = fetchBaseQuery({
    baseUrl: VITE_API_URL,
    paramsSerializer(params) {
      return qs.stringify(params, { allowDots: true });
    },
    prepareHeaders: (headers, { getState }) => {
      // By default, if we have a token in the store, let's use that for authenticated requests
      const {
        auth: { token },
      } = getState() as COIStore;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  });

  return async (args, api, extraOptions) => {
    const {
      auth: { token: requestToken },
    } = api.getState() as COIStore;

    const initialResult = await query(args, api, extraOptions);

    if (initialResult.error?.status !== 401) {
      return initialResult;
    }

    const refreshedAccessToken = await tryRefreshAuthToken(api, requestToken);

    if (!refreshedAccessToken) {
      return initialResult;
    }

    return query(args, api, extraOptions);
  };
})();
